import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FiPlus, FiTrash2, FiPrinter, FiDownload, FiRotateCcw, FiAlertTriangle,
  FiShare2, FiCheck, FiCornerUpLeft, FiArrowDown, FiArrowRight, FiArrowLeft,
  FiChevronDown, FiChevronUp,
} from "react-icons/fi";

import styles from "./TipSplitPage.module.css";
import { STRINGS } from "./strings";
import ToolFeedback from "../toolFeedback/ToolFeedback";
import { buildPoolReceipt, buildTipOutReceipt, buildSignSheet, downloadReceiptPdf } from "./receipt";
import { KEYS, readJSON, writeJSON, mergeJSON, storageAvailable } from "../../lib/storage";
import {
  computePoolSplit,
  computeTipOut,
  parseMoneyToCents,
  parseHundredths,
  formatMoney,
  formatHundredths,
  weightOf,
  isBasis,
} from "../../lib/formulas/tipSplit";

const SITE = "https://www.danielrayrangel.com";

// v1 ships English only, by decision. Every string the page and the receipt use
// already comes from the bundle, so v2 is: write STRINGS.es, add an /es route,
// and pick the bundle off the URL locale the way the homepage does.
const t = STRINGS.en;

// Every number the user types lives in state as the text they typed. Parsing on
// each keystroke and formatting the result back into the box makes fields
// impossible to fill: "10" becomes "1.00" before the zero is typed.

// Starter roles, not starter percentages. Points are a house convention that a
// manager recognizes and adjusts; a tip-out percentage is not ours to guess, so
// the rules list starts empty.
//
// The front of house, plus the two support jobs nobody thinks to add until the
// tip-out is already written. A role that never gets tipped costs nothing to
// leave sitting here — no rule points at it, so it asks nobody for anything —
// and one nobody uses deletes in a tap. Back of house is not in the list on
// purpose: whether the kitchen can be in a pool at all turns on the tip credit
// and the state, so it is a role a house adds deliberately, not one we hand
// them. Ids are append-only: a saved roster points at them by id, so role-3 has
// to stay Bussers forever.
const DEFAULT_ROLES = [
  { id: "role-1", name: "Server", defaultPoints: "1.00" },
  { id: "role-2", name: "Bar", defaultPoints: "1.00" },
  { id: "role-3", name: "Bussers", defaultPoints: "0.50" },
  { id: "role-4", name: "Food runners", defaultPoints: "0.50" },
  { id: "role-5", name: "Barback", defaultPoints: "0.50" },
  { id: "role-6", name: "Host", defaultPoints: "0.50" },
];

// Ranges from the build spec. Out-of-range is not always a mistake — a house can
// legitimately run 3.5 points — so only the physically impossible blocks the
// split; the merely unusual gets flagged and left alone.
const HOURS_MAX_CENTI = 2400;
const POINTS_MIN_CENTI = 5;
const POINTS_MAX_CENTI = 300;
const PCT_MIN_CENTI = 25;
const PCT_SANE_MAX_CENTI = 2500;
const PCT_HARD_MAX_CENTI = 10000;

// Long enough to notice the bar, read it, and reach it with a thumb.
const UNDO_MS = 9000;
const COPIED_MS = 2400;

const BASIS_OPTIONS = [
  { value: "tips", label: () => t.rules.ofTips },
  { value: "food", label: () => t.rules.ofFood },
  { value: "liquor", label: () => t.rules.ofLiquor },
  { value: "sales", label: () => t.rules.ofSales },
];

const NO_NEEDS = {
  contributes: false, receives: false, tips: false, sales: false, food: false, liquor: false,
};

const blankEntry = () => ({
  working: true, hours: "", tips: "", sales: "", food: "", liquor: "",
});

// Ids are only ever minted in event handlers, so nothing here runs during the
// prerender pass or differs between server markup and hydration.
function nextId(prefix, existing) {
  let max = 0;
  existing.forEach((item) => {
    const m = new RegExp(`^${prefix}-(\\d+)$`).exec(item.id || "");
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `${prefix}-${max + 1}`;
}

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const fileSafe = (text) =>
  String(text || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .toLowerCase();

const TipSplitPage = () => {
  const [mode, setMode] = useState("tipout");
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [roster, setRoster] = useState([
    { id: "p-1", name: "", roleId: "role-1", points: "1.00" },
  ]);
  const [rules, setRules] = useState([]);
  const [entries, setEntries] = useState({ "p-1": blankEntry() });
  const [shiftLabel, setShiftLabel] = useState("");
  const [poolText, setPoolText] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [bulkHours, setBulkHours] = useState("");

  const [confirmReset, setConfirmReset] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasStorage, setHasStorage] = useState(true);
  const [undo, setUndo] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Panels a returning manager has already read once. Open on a first visit,
  // then whatever they left them as, because the crew rarely changes and the
  // walkthrough is only new the first night.
  const [guideOpen, setGuideOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(true);
  const [rolesOpen, setRolesOpen] = useState(false);

  const loaded = useRef(false);
  const workspaceRef = useRef(null);
  const resultsRef = useRef(null);
  const nameRefs = useRef({});
  const undoTimer = useRef(null);
  const feedbackBtnRef = useRef(null);
  const [focusPerson, setFocusPerson] = useState(null);

  const reduceMotion = useReducedMotion();

  /* ── Load once, then persist on change ─────────────────────────────────── */

  useEffect(() => {
    setHasStorage(storageAvailable());

    const saved = readJSON(KEYS.tipSplit, null);
    if (saved && typeof saved === "object") {
      if (saved.mode === "pool" || saved.mode === "tipout") setMode(saved.mode);
      if (Array.isArray(saved.roles) && saved.roles.length) {
        setRoles(
          saved.roles.map((r) => ({
            id: r.id,
            name: r.name || "",
            defaultPoints: formatHundredths(r.defaultPointsCenti ?? 100),
          }))
        );
      }
      if (Array.isArray(saved.roster) && saved.roster.length) {
        setRoster(
          saved.roster.map((p) => ({
            id: p.id,
            name: p.name || "",
            roleId: p.roleId,
            points: formatHundredths(p.pointsCenti ?? 100),
          }))
        );
      }
      if (Array.isArray(saved.tipoutRules)) {
        setRules(
          saved.tipoutRules.map((r) => ({
            id: r.id,
            fromRoleId: r.fromRoleId,
            toRoleId: r.toRoleId,
            // Rules saved before food and liquor were separate bases read the
            // one total-sales figure, which is exactly what "sales" still means.
            basis: isBasis(r.basis) ? r.basis : "tips",
            pct: formatHundredths(r.pctCenti ?? 1000),
          }))
        );
      }

      // Which panels were left open. Written by a later version than the one
      // that may have saved this object, so every field is checked, not assumed.
      const ui = saved.ui;
      if (ui && typeof ui === "object") {
        if (typeof ui.guideOpen === "boolean") setGuideOpen(ui.guideOpen);
        if (typeof ui.rosterOpen === "boolean") setRosterOpen(ui.rosterOpen);
        if (typeof ui.rolesOpen === "boolean") setRolesOpen(ui.rolesOpen);
      }

      const last = saved.lastShift;
      if (last && typeof last === "object") {
        setShiftLabel(last.dateLabel || "");
        // Amounts come back formatted from their stored integer form, so a
        // restored shift reads the same as a freshly typed one.
        setPoolText(last.poolCents ? formatHundredths(last.poolCents) : "");
        const restored = {};
        (last.entries || []).forEach((e) => {
          restored[e.personId] = {
            working: e.working !== false,
            hours: e.hoursCenti ? formatHundredths(e.hoursCenti) : "",
            tips: e.tipsCents ? formatHundredths(e.tipsCents) : "",
            sales: e.salesCents ? formatHundredths(e.salesCents) : "",
            food: e.foodSalesCents ? formatHundredths(e.foodSalesCents) : "",
            liquor: e.liquorSalesCents ? formatHundredths(e.liquorSalesCents) : "",
          };
        });
        if (Object.keys(restored).length) setEntries(restored);
      }
    }

    const profile = readJSON(KEYS.profile, null);
    if (profile && profile.businessName) setBusinessName(profile.businessName);

    loaded.current = true;
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    writeJSON(KEYS.tipSplit, {
      mode,
      roles: roles.map((r) => ({
        id: r.id,
        name: r.name,
        defaultPointsCenti: parseHundredths(r.defaultPoints) ?? 100,
      })),
      roster: roster.map((p) => ({
        id: p.id,
        name: p.name,
        roleId: p.roleId,
        pointsCenti: parseHundredths(p.points) ?? 100,
      })),
      tipoutRules: rules.map((r) => ({
        id: r.id,
        fromRoleId: r.fromRoleId,
        toRoleId: r.toRoleId,
        basis: r.basis,
        pctCenti: parseHundredths(r.pct) ?? 0,
      })),
      ui: { guideOpen, rosterOpen, rolesOpen },
      lastShift: {
        dateLabel: shiftLabel,
        poolCents: parseMoneyToCents(poolText) ?? 0,
        entries: roster.map((p) => {
          const e = entries[p.id] || blankEntry();
          return {
            personId: p.id,
            working: e.working,
            hoursCenti: parseHundredths(e.hours) ?? 0,
            tipsCents: parseMoneyToCents(e.tips) ?? 0,
            salesCents: parseMoneyToCents(e.sales) ?? 0,
            foodSalesCents: parseMoneyToCents(e.food) ?? 0,
            liquorSalesCents: parseMoneyToCents(e.liquor) ?? 0,
          };
        }),
      },
    });
  }, [
    mode, roles, roster, rules, entries, shiftLabel, poolText,
    guideOpen, rosterOpen, rolesOpen,
  ]);

  const onBusinessName = (value) => {
    setBusinessName(value);
    // Shared with the other tools, so merge rather than overwrite the profile.
    if (loaded.current) mergeJSON(KEYS.profile, { businessName: value });
  };

  /* ── Taking something back ────────────────────────────────────────────────
     Deleting a person and clearing a shift both throw away numbers somebody
     typed, off a single tap, on a target the size of a fingernail. Neither one
     asks a question now; both hand back an undo instead, which is faster to use
     and impossible to mis-tap into.
     ─────────────────────────────────────────────────────────────────────── */

  useEffect(() => () => window.clearTimeout(undoTimer.current), []);

  const armUndo = useCallback((message, restore) => {
    window.clearTimeout(undoTimer.current);
    setUndo({ message, restore });
    undoTimer.current = window.setTimeout(() => setUndo(null), UNDO_MS);
  }, []);

  const runUndo = () => {
    if (!undo) return;
    window.clearTimeout(undoTimer.current);
    undo.restore();
    setUndo(null);
  };

  /* ── Roster and role editing ───────────────────────────────────────────── */

  const roleName = useCallback(
    (id) => {
      const role = roles.find((r) => r.id === id);
      return role ? role.name : "";
    },
    [roles]
  );

  const displayName = useCallback(
    (person, index) => person.name.trim() || `Person ${index + 1}`,
    []
  );

  // Adding somebody puts the cursor in their name box, so building a crew on a
  // phone is add-type-add-type instead of add-aim-tap-type.
  const addPerson = () => {
    const id = nextId("p", roster);
    const role = roles[0];
    setRoster((list) => [
      ...list,
      { id, name: "", roleId: role ? role.id : "", points: role ? role.defaultPoints : "1.00" },
    ]);
    setEntries((map) => ({ ...map, [id]: blankEntry() }));
    setRosterOpen(true);
    setFocusPerson(id);
  };

  useEffect(() => {
    if (!focusPerson) return;
    const el = nameRefs.current[focusPerson];
    if (el && el.focus) el.focus();
    setFocusPerson(null);
  }, [focusPerson]);

  const updatePerson = (id, patch) =>
    setRoster((list) => list.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const removePerson = (id) => {
    const index = roster.findIndex((p) => p.id === id);
    if (index < 0) return;
    const person = roster[index];
    const entry = entries[id];
    setRoster((list) => list.filter((p) => p.id !== id));
    setEntries((map) => {
      const next = { ...map };
      delete next[id];
      return next;
    });
    armUndo(t.results.undoRemoved(displayName(person, index)), () => {
      setRoster((list) => {
        const next = list.slice();
        next.splice(Math.min(index, next.length), 0, person);
        return next;
      });
      setEntries((map) => ({ ...map, [id]: entry || blankEntry() }));
    });
  };

  // Changing someone's role pulls that role's default points across, which is
  // what a manager expects when they move a server onto the bar for a night.
  const changeRole = (id, roleId) => {
    const role = roles.find((r) => r.id === roleId);
    updatePerson(id, { roleId, points: role ? role.defaultPoints : "1.00" });
  };

  const updateEntry = (id, patch) =>
    setEntries((map) => ({ ...map, [id]: { ...(map[id] || blankEntry()), ...patch } }));

  const addRole = () => {
    const id = nextId("role", roles);
    setRoles((list) => [...list, { id, name: "", defaultPoints: "1.00" }]);
  };

  const updateRole = (id, patch) =>
    setRoles((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const roleInUse = (id) =>
    roster.some((p) => p.roleId === id) ||
    rules.some((r) => r.fromRoleId === id || r.toRoleId === id);

  const removeRole = (id) => {
    if (roleInUse(id)) return;
    setRoles((list) => list.filter((r) => r.id !== id));
  };

  const addRule = () => {
    const from = roles[0];
    const to = roles.find((r) => r.id !== (from && from.id)) || from;
    if (!from || !to) return;
    setRules((list) => [
      ...list,
      {
        id: nextId("rule", list),
        fromRoleId: from.id,
        toRoleId: to.id,
        basis: "tips",
        pct: "10.00",
      },
    ]);
  };

  const updateRule = (id, patch) =>
    setRules((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRule = (id) => setRules((list) => list.filter((r) => r.id !== id));

  /* ── What each mode asks each person for ───────────────────────────────────
     Points and hours only mean something for a role that gets paid out of a
     pool; sales only for a role that pays into one. Working that out once here
     keeps the shift form, the validation, and the math asking for exactly the
     same set of numbers.

     Tips are the exception to "only what your rules read": a bartender is
     handed cash across the bar all night and still gets the servers' tip-out on
     top of it. The form used to ask nobody but the payers for tips, so the
     bar's own money had nowhere to go and their ledger came out short. Support
     roles are asked too, optionally — blank means they took nothing directly,
     which is the busser's usual night.
     ─────────────────────────────────────────────────────────────────────── */

  const needsByRole = useMemo(() => {
    const map = new Map();
    const ensure = (id) => {
      if (!map.has(id)) map.set(id, { ...NO_NEEDS });
      return map.get(id);
    };
    rules.forEach((rule) => {
      // A role pointed at itself is blocked, not calculated, so it does not get
      // to make the form ask for numbers nothing will use.
      if (rule.fromRoleId === rule.toRoleId) return;
      const from = ensure(rule.fromRoleId);
      from.contributes = true;
      // Take-home is their own tips minus the deductions, so a contributor is
      // always asked for tips even when every rule against them reads sales.
      from.tips = true;
      if (rule.basis !== "tips") from[rule.basis] = true;
      ensure(rule.toRoleId).receives = true;
    });
    return map;
  }, [rules]);

  const fieldsFor = useCallback(
    (person) => {
      const need = needsByRole.get(person.roleId) || NO_NEEDS;
      if (mode === "pool") {
        return {
          hours: true, tips: false, sales: false, food: false, liquor: false,
          idle: false, optional: {},
        };
      }
      return {
        hours: need.receives,
        tips: need.tips || need.receives,
        sales: need.sales,
        food: need.food,
        liquor: need.liquor,
        idle: !need.contributes && !need.receives,
        // Hours sharpen a support split without being needed to make one, and a
        // support role's own tips are theirs whether or not anyone types them.
        // Neither can hold up the shift, so neither is counted as owed below.
        optional: { hours: true, tips: !need.tips },
      };
    },
    [mode, needsByRole]
  );

  /* ── Where points can and cannot reach ────────────────────────────────────
     Points are a per-hour multiplier on a share of a pool, so they only mean
     something for somebody being paid out of one. In tip-out mode a
     contributor's take-home is their tips minus their deductions and nothing
     else — weightOf is never called on them — so their points box was fully
     editable, saved, and range-warned while being incapable of moving a cent.
     The form now goes flat where it cannot read, the way step 3 already only
     asks for the figures a person's own rules need.
     ─────────────────────────────────────────────────────────────────────── */

  const pointsMatter = useCallback(
    (person) => mode === "pool" || Boolean((needsByRole.get(person.roleId) || NO_NEEDS).receives),
    [mode, needsByRole]
  );

  const workingPeople = useMemo(
    () => roster.filter((p) => (entries[p.id] || blankEntry()).working),
    [roster, entries]
  );

  // Percentages as integers, kept out of the render path so a typed "10"
  // parses once per change rather than once per person.
  const numericRules = useMemo(
    () => rules.map((r) => ({ ...r, pctCenti: parseHundredths(r.pct) ?? 0 })),
    [rules]
  );

  /* ── How much of the shift is actually filled in ───────────────────────────
     A crew of nine is nine scroll-lengths of boxes with a keyboard covering
     half the screen. The counter and the per-person tick say how far along you
     are without scrolling back up to look.

     It counts the numbers the split is actually waiting on. A busser whose only
     boxes are optional is not holding anything up, and saying "3 of 9" at a
     manager over boxes nobody has to fill sends them hunting for a problem that
     is not there.
     ─────────────────────────────────────────────────────────────────────── */

  const progress = useMemo(() => {
    const filled = new Set();
    let asked = 0;
    let done = 0;
    workingPeople.forEach((p) => {
      const f = fieldsFor(p);
      const wants = ["hours", "tips", "food", "liquor", "sales"].filter(
        (k) => f[k] && !f.optional[k]
      );
      if (!wants.length) return;
      asked += 1;
      const entry = entries[p.id] || blankEntry();
      if (wants.every((k) => String(entry[k]).trim() !== "")) {
        done += 1;
        filled.add(p.id);
      }
    });
    return { asked, done, filled };
  }, [workingPeople, entries, fieldsFor]);

  // Closing shift, most of the crew clocked the same hours. Typing it once and
  // correcting the two people who did not beats typing it eight times.
  const hoursWanted = useMemo(
    () => workingPeople.filter((p) => fieldsFor(p).hours),
    [workingPeople, fieldsFor]
  );

  const applyHoursToAll = () => {
    const value = bulkHours.trim();
    if (!value) return;
    setEntries((map) => {
      const next = { ...map };
      hoursWanted.forEach((p) => {
        next[p.id] = { ...(next[p.id] || blankEntry()), hours: value };
      });
      return next;
    });
  };

  /* ── Field-level validation ────────────────────────────────────────────────
     The old copy told people to check the highlighted boxes without ever
     highlighting one. Every box the form actually shows gets checked here, and
     only the boxes on screen can block the split — a number nobody can see must
     never be able to stop the shift.

     Errors are also kept in the order they were found, which is document order,
     so the sticky bar can send a thumb straight at the first one.
     ─────────────────────────────────────────────────────────────────────── */

  const validation = useMemo(() => {
    const issues = {};
    const errorOrder = [];
    const err = (key, message) => {
      if (!issues[key] || issues[key].level !== "error") errorOrder.push(key);
      issues[key] = { level: "error", message };
    };
    const warn = (key, message) => {
      if (!issues[key]) issues[key] = { level: "warn", message };
    };
    const money = (key, text) => {
      const cents = parseMoneyToCents(text);
      if (cents === null) err(key, t.validation.notNumber);
      else if (cents < 0) err(key, t.validation.negative);
      return cents;
    };
    const hundredths = (key, text) => {
      const value = parseHundredths(text);
      if (value === null) err(key, t.validation.notNumber);
      else if (value < 0) err(key, t.validation.negative);
      return value;
    };
    const pointsRange = (key, value) => {
      if (value !== null && value > 0 && (value < POINTS_MIN_CENTI || value > POINTS_MAX_CENTI)) {
        warn(key, t.validation.pointsRange);
      }
    };

    if (mode === "pool") money("pool", poolText);

    roles.forEach((r) => {
      const key = `rolePoints:${r.id}`;
      pointsRange(key, hundredths(key, r.defaultPoints));
    });

    // A points box that cannot move a cent does not get to flag anything, and
    // certainly does not get to block the split. Same rule the shift form runs
    // on: only what is being asked for counts.
    roster.forEach((p) => {
      if (!pointsMatter(p)) return;
      const key = `points:${p.id}`;
      pointsRange(key, hundredths(key, p.points));
    });

    // Tip-out splits a support pool by hours only if somebody in that role gave
    // hours. Where one person in a role did and another did not, the blank one
    // is about to be paid nothing — say so rather than let it happen quietly.
    const rolesOnHours = new Set();
    if (mode === "tipout") {
      workingPeople.forEach((p) => {
        if (!fieldsFor(p).hours) return;
        if ((parseHundredths((entries[p.id] || blankEntry()).hours) ?? 0) > 0) {
          rolesOnHours.add(p.roleId);
        }
      });
    }

    workingPeople.forEach((p) => {
      const fields = fieldsFor(p);
      const entry = entries[p.id] || blankEntry();
      if (fields.hours) {
        const key = `hours:${p.id}`;
        const value = hundredths(key, entry.hours);
        if (value !== null && value > HOURS_MAX_CENTI) err(key, t.validation.hoursTooHigh);
        else if (value === 0 && rolesOnHours.has(p.roleId)) {
          warn(key, t.validation.hoursBlankAmongHours);
        }
      }
      if (fields.tips) money(`tips:${p.id}`, entry.tips);
      if (fields.food) money(`food:${p.id}`, entry.food);
      if (fields.liquor) money(`liquor:${p.id}`, entry.liquor);
      if (fields.sales) money(`sales:${p.id}`, entry.sales);
    });

    if (mode === "tipout") {
      rules.forEach((r) => {
        if (r.fromRoleId === r.toRoleId) err(`ruleSelf:${r.id}`, t.rules.sameRole);
        const key = `pct:${r.id}`;
        const value = hundredths(key, r.pct);
        if (value === null || value < 0) return;
        if (value > PCT_HARD_MAX_CENTI) err(key, t.validation.pctTooHigh);
        else if (value > 0 && (value < PCT_MIN_CENTI || value > PCT_SANE_MAX_CENTI)) {
          warn(key, t.validation.pctRange);
        }
      });
    }

    return { issues, errorOrder, blocked: errorOrder.length > 0 };
  }, [mode, poolText, roles, roster, rules, entries, workingPeople, fieldsFor, pointsMatter]);

  const { issues, errorOrder } = validation;

  const issueClass = useCallback(
    (key) => {
      const issue = issues[key];
      if (!issue) return "";
      return issue.level === "error" ? styles.inputInvalid : styles.inputWarn;
    },
    [issues]
  );

  /* ── The math ──────────────────────────────────────────────────────────── */

  const computed = useMemo(() => {
    const nothing = (hint) => ({ result: null, error: null, problem: null, emptyHint: hint });

    if (!workingPeople.length) return nothing(null);
    if (validation.blocked) {
      return { result: null, error: t.errors.CHECK_FIELDS, problem: null, emptyHint: null };
    }

    const numeric = workingPeople.map((p) => {
      const entry = entries[p.id] || blankEntry();
      const fields = fieldsFor(p);
      // Only what the form asked for is allowed into the math. A figure left
      // behind by a rule that has since been deleted is invisible on screen, so
      // it must not be able to move money either.
      const take = (flag, text, parse) => (flag ? parse(text) ?? 0 : 0);
      return {
        id: p.id,
        name: displayName(p, roster.indexOf(p)),
        roleId: p.roleId,
        role: roleName(p.roleId),
        pointsCenti: parseHundredths(p.points) ?? 0,
        hoursCenti: take(fields.hours, entry.hours, parseHundredths),
        tipsCents: take(fields.tips, entry.tips, parseMoneyToCents),
        salesCents: take(fields.sales, entry.sales, parseMoneyToCents),
        foodSalesCents: take(fields.food, entry.food, parseMoneyToCents),
        liquorSalesCents: take(fields.liquor, entry.liquor, parseMoneyToCents),
      };
    });

    try {
      if (mode === "pool") {
        // No hours yet is an unfinished form, not a mistake. It used to greet
        // every first-time visitor with a red error before they typed anything.
        if (numeric.reduce((a, p) => a + weightOf(p), 0) === 0) {
          return nothing(t.results.needHours);
        }
        return {
          result: computePoolSplit({ poolCents: parseMoneyToCents(poolText) ?? 0, people: numeric }),
          error: null,
          problem: null,
          emptyHint: null,
        };
      }

      if (!rules.length) return nothing(t.results.needRules);

      const anyMoney = numeric.some(
        (p) => p.tipsCents > 0 || p.salesCents > 0 || p.foodSalesCents > 0 || p.liquorSalesCents > 0
      );
      if (!anyMoney) return nothing(t.results.needTips);

      const result = computeTipOut({ people: numeric, roles, rules: numericRules });

      // One over-deducted person keeps their own ledger on screen instead of
      // blanking everyone else's. Nothing is printable until it is resolved.
      if (!result.valid) {
        const first = result.problems[0];
        return { result, error: null, problem: t.errors.NEGATIVE_TAKE_HOME(first), emptyHint: null };
      }
      return { result, error: null, problem: null, emptyHint: null };
    } catch (err) {
      const entry = t.errors[err.code];
      const message =
        typeof entry === "function" ? entry(err.details || {}) : entry || t.errors.GENERIC;
      return { result: null, error: message, problem: null, emptyHint: null };
    }
  }, [
    mode, roster, entries, poolText, rules, numericRules, roles, workingPeople,
    roleName, displayName, fieldsFor, validation.blocked,
  ]);

  const { result, error, problem, emptyHint } = computed;
  const usable = Boolean(result) && (mode === "pool" || result.valid);

  /* ── Getting around a page taller than the phone ──────────────────────── */

  const scrollTo = (node, block = "start") => {
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block });
    }
  };

  const showResults = () => scrollTo(resultsRef.current);

  const jumpToIssue = () => {
    const key = errorOrder[0];
    if (!key) return showResults();
    // A box inside a collapsed panel cannot be scrolled to, so open its panel
    // first and let React put it on screen before going looking for it. Only
    // the two points boxes live somewhere foldable; everything else is always
    // on screen.
    if (key.startsWith("points:") || key.startsWith("rolePoints:")) setRosterOpen(true);
    if (key.startsWith("rolePoints:")) setRolesOpen(true);
    window.setTimeout(() => {
      const el = document.getElementById(`f-${key}`);
      if (!el) return;
      scrollTo(el, "center");
      if (el.focus) el.focus({ preventScroll: true });
    }, 0);
  };

  // Enter walks down the form the way Tab does, for the Android keyboard and for
  // anyone on a laptop. iOS shows a decimal pad with no return key at all, which
  // is exactly why the roster autofocus and "same hours for everyone" matter.
  const onFieldKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    const root = workspaceRef.current;
    if (!root) return;
    event.preventDefault();
    const fields = Array.from(
      root.querySelectorAll('input:not([type="checkbox"]):not([disabled]), select:not([disabled])')
    ).filter((el) => el === event.target || el.offsetParent !== null);
    const next = fields[fields.indexOf(event.target) + 1];
    if (next && next.focus) next.focus();
  };

  /* ── Receipt ───────────────────────────────────────────────────────────── */

  // What each person actually walks out with, used by the sign sheet. In pool
  // mode that is their share; in tip-out mode it is their take-home plus
  // anything they picked up from a pool they are in.
  const payouts = useMemo(() => {
    const map = new Map();
    if (!usable) return map;
    if (mode === "pool") {
      result.rows.forEach((r) => map.set(r.id, r.shareCents));
    } else {
      result.contributors.forEach((c) => map.set(c.id, c.takeHomeCents));
      result.pools.forEach((pool) =>
        pool.recipients.forEach((r) => map.set(r.id, (map.get(r.id) || 0) + r.shareCents))
      );
    }
    return map;
  }, [result, mode, usable]);

  // The breakdown, built once for whoever wants it: the print sheet, the PDF,
  // and the text that gets shared into a group chat.
  const buildBreakdown = useCallback(() => {
    const shared = {
      t,
      businessName: businessName.trim(),
      shiftLabel: shiftLabel.trim(),
      result,
    };
    return mode === "pool" ? buildPoolReceipt(shared) : buildTipOutReceipt(shared);
  }, [mode, businessName, shiftLabel, result]);

  const receiptLines = useMemo(() => {
    const shared = { t, businessName: businessName.trim(), shiftLabel: shiftLabel.trim() };

    if (pendingPrint === "blank") {
      return buildSignSheet({
        ...shared,
        people: workingPeople.map((p) => ({
          name: displayName(p, roster.indexOf(p)),
          role: roleName(p.roleId),
          shareCents: payouts.has(p.id) ? payouts.get(p.id) : null,
        })),
      });
    }
    if (!usable) return [];
    return buildBreakdown();
  }, [pendingPrint, usable, businessName, shiftLabel, workingPeople, roster, payouts, roleName, displayName, buildBreakdown]);

  // The print dialog has to open after the receipt for this button has actually
  // rendered, so the click sets what to print and the effect does the printing.
  useEffect(() => {
    if (!pendingPrint) return undefined;
    const timer = window.setTimeout(() => {
      window.print();
      setPendingPrint(null);
    }, 60);
    return () => window.clearTimeout(timer);
  }, [pendingPrint]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const canPrint = usable && !error && !problem;

  /* ── Getting the numbers off the phone ─────────────────────────────────────
     A manager standing at a POS at 1am is texting these numbers to the group
     chat, not looking for a printer, so the share sheet leads and paper follows.
     The receipt is already plain monospace text, which is exactly what pastes
     into a message. Nothing is uploaded: the share sheet and the clipboard are
     both local to the device.
     ─────────────────────────────────────────────────────────────────────── */

  const handleShare = async () => {
    if (!canPrint) return;
    const text = buildBreakdown()
      .map((line) => line.text)
      .join("\n")
      .replace(/\n+$/, "");
    const title =
      [businessName.trim(), shiftLabel.trim()].filter(Boolean).join(" - ") || t.results.shareTitle;

    try {
      if (navigator.share) {
        await navigator.share({ title, text });
        return;
      }
    } catch (err) {
      // Dismissing the share sheet is not a failure, and it is the one case
      // where falling through to the clipboard would be the wrong answer.
      if (err && err.name === "AbortError") return;
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Tip split share failed", err);
    }
  };

  const handleDownload = async () => {
    if (!usable || busy) return;
    setBusy(true);
    try {
      const label = fileSafe(shiftLabel) || fileSafe(businessName) || "shift";
      await downloadReceiptPdf(buildBreakdown(), `tip-split-${label}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Tip split PDF failed", err);
    } finally {
      setBusy(false);
    }
  };

  const resetShift = () => {
    const previous = { poolText, shiftLabel, entries };
    setPoolText("");
    setShiftLabel("");
    setBulkHours("");
    setEntries((map) => {
      const next = {};
      Object.keys(map).forEach((id) => {
        next[id] = { ...blankEntry(), working: map[id].working };
      });
      return next;
    });
    setConfirmReset(false);
    armUndo(t.results.undoReset, () => {
      setPoolText(previous.poolText);
      setShiftLabel(previous.shiftLabel);
      setEntries(previous.entries);
    });
  };

  /* ── Small render helpers ──────────────────────────────────────────────── */

  const numberField = (label, value, onChange, opts = {}) => {
    const issue = opts.issueKey ? issues[opts.issueKey] : null;
    // Formatting on every keystroke makes a box impossible to fill, but doing it
    // once the box is left turns "50" into "50.00" and quietly confirms the
    // number was read as money rather than as something to argue with later.
    const onBlur = () => {
      if (!opts.money) return;
      const text = String(value).trim();
      if (!text) return;
      const cents = parseMoneyToCents(text);
      if (cents === null || cents < 0) return;
      const next = formatHundredths(cents);
      if (next !== value) onChange(next);
    };
    return (
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {label}
          {opts.optional && <span className={styles.optionalMark}>{t.roster.optional}</span>}
        </span>
        <input
          id={opts.issueKey ? `f-${opts.issueKey}` : undefined}
          className={`${styles.input} ${opts.issueKey ? issueClass(opts.issueKey) : ""}`}
          type="text"
          inputMode="decimal"
          enterKeyHint="next"
          value={value}
          placeholder={opts.ph || "0"}
          // Pinned so the caption can carry an "optional" mark without the box
          // answering to a different name than the one printed above it.
          aria-label={label}
          aria-invalid={issue && issue.level === "error" ? "true" : undefined}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onFieldKeyDown}
          onBlur={onBlur}
        />
        {issue && (
          <span className={issue.level === "error" ? styles.fieldIssue : styles.fieldIssueWarn}>
            {issue.message}
          </span>
        )}
      </label>
    );
  };

  const money = (cents) => formatMoney(cents);

  // The amount is the thing people are actually watching, so it moves when it
  // changes instead of silently becoming a different number.
  const amount = (cents, className) => (
    <motion.span
      key={cents}
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
    >
      {money(cents)}
    </motion.span>
  );

  /* ── The standing status line ──────────────────────────────────────────────
     The boxes and the answer cannot both be on a phone screen, so the answer
     used to be invisible while you typed. This rides the bottom of the workspace and
     says the one thing worth knowing, and tapping it goes to the thing it
     named — the first red box, or the split.
     ─────────────────────────────────────────────────────────────────────── */

  const bar = (() => {
    if (errorOrder.length) {
      return {
        tone: styles.barAlert,
        text: errorOrder.length === 1 ? t.bar.fixOne : t.bar.fixMany(errorOrder.length),
        action: t.bar.goFix,
        onClick: jumpToIssue,
      };
    }
    if (error || problem) {
      return { tone: styles.barAlert, text: t.bar.problem, action: t.bar.see, onClick: showResults };
    }
    if (usable) {
      return {
        tone: styles.barReady,
        text: t.bar.ready(payouts.size || workingPeople.length),
        action: t.bar.see,
        onClick: showResults,
      };
    }
    return { tone: styles.barIdle, text: t.bar.idle, action: null, onClick: null };
  })();

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <>
      <Helmet>
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={`${SITE}/tools/tip-split`} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE}/tools/tip-split`} />
        <meta property="og:title" content={t.meta.ogTitle} />
        <meta property="og:description" content={t.meta.ogDescription} />
        <meta property="og:site_name" content="Exhale Software Solutions" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.meta.ogTitle} />
        <meta name="twitter:description" content={t.meta.ogDescription} />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&display=swap"
          rel="stylesheet"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Tip Split Calculator",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Any (web browser)",
            url: `${SITE}/tools/tip-split`,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            featureList:
              "Tip pool split by weighted hours; tip-out percentages of tips, food sales, or liquor sales; saved roster; printable per-person breakdown; PDF download",
            provider: {
              "@type": "Organization",
              name: "Exhale Software Solutions",
              url: SITE,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
        {/* Mirrors the visible path back to /tools in the hero. Search results
            show the trail in place of the raw URL, so the tool reads as part of
            a collection rather than a loose page. */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Free Tools", item: `${SITE}/tools` },
              {
                "@type": "ListItem",
                position: 3,
                name: "Tip Split Calculator",
                item: `${SITE}/tools/tip-split`,
              },
            ],
          })}
        </script>
      </Helmet>

      <main className={styles.page}>
        {/* ── HERO ── */}
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <a href="/tools" className={styles.backLink}>
              <FiArrowLeft aria-hidden="true" /> {t.hero.backLink}
            </a>
            <p className={styles.eyebrow}>
              {t.hero.badges.map((badge, i) => (
                <React.Fragment key={badge}>
                  {i > 0 && <span className={styles.eyebrowDot}>✦</span>}
                  <span className={i === 0 ? styles.eyebrowMark : undefined}>{badge}</span>
                </React.Fragment>
              ))}
            </p>
            <h1 className={styles.heroTitle}>
              {t.hero.titleLine1}
              <br />
              <em>{t.hero.titleLine2}</em>
            </h1>
            <p className={styles.heroSub}>{t.hero.sub}</p>
          </div>
        </section>

        {/* ── WORKSPACE ── */}
        <section className={styles.workspace} ref={workspaceRef}>
          <div className={styles.stack}>
            {/* Mode */}
            <div className={styles.panel}>
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 01</span>
                <span className={styles.figDash}>/</span> {t.modes.label}
              </p>
              <div className={styles.segmented}>
                <button
                  type="button"
                  className={`${styles.segBtn} ${mode === "tipout" ? styles.segActive : ""}`}
                  onClick={() => setMode("tipout")}
                >
                  {t.modes.tipout}
                </button>
                <button
                  type="button"
                  className={`${styles.segBtn} ${mode === "pool" ? styles.segActive : ""}`}
                  onClick={() => setMode("pool")}
                >
                  {t.modes.pool}
                </button>
                
              </div>
              <p className={styles.hint}>
                {mode === "pool" ? t.modes.poolHint : t.modes.tipoutHint}
              </p>

              {/* The order to do this in, which the tool previously left the
                  manager to infer from which boxes happened to be on screen.
                  Folds away once it has been read, and stays folded. */}
              <details
                className={styles.guide}
                open={guideOpen}
                onToggle={(e) => setGuideOpen(e.currentTarget.open)}
              >
                <summary className={styles.guideSummary}>
                  <span>{t.guide.title}</span>
                  <FiChevronDown className={styles.guideChevron} aria-hidden="true" />
                </summary>
                <ol className={styles.guideList}>
                  {t.guide[mode === "pool" ? "pool" : "tipout"].map((line, i) => (
                    <li key={i} className={styles.guideItem}>
                      <span className={styles.guideNum}>{i + 1}</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ol>
              </details>
            </div>

            {/* Step 1 — the money and the rules.
                Ahead of the roster on purpose. The pool total and the tip-out
                rules are the part somebody punches in standing at the pass;
                building a crew is a close-out job. Rules belong up here on
                their own merits too: they are written against roles, which
                exist whether or not anybody has been added yet. */}
            <div className={styles.panel}>
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 02</span>
                <span className={styles.figDash}>/</span> {t.steps.tonight}
              </p>
              <p className={styles.panelSub}>{t.steps.tonightSub[mode]}</p>

              <div className={styles.shiftHead}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t.shift.labelField}</span>
                  <input
                    className={styles.input}
                    type="text"
                    value={shiftLabel}
                    placeholder={t.shift.labelPlaceholder}
                    enterKeyHint="next"
                    onChange={(e) => setShiftLabel(e.target.value)}
                    onKeyDown={onFieldKeyDown}
                  />
                </label>
                {mode === "pool" &&
                  numberField(t.shift.pool, poolText, setPoolText, {
                    ph: t.shift.poolPlaceholder,
                    issueKey: "pool",
                    money: true,
                  })}
              </div>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t.shift.businessField}</span>
                <input
                  className={styles.input}
                  type="text"
                  value={businessName}
                  placeholder={t.shift.businessPlaceholder}
                  enterKeyHint="next"
                  onChange={(e) => onBusinessName(e.target.value)}
                  onKeyDown={onFieldKeyDown}
                />
                <span className={styles.fieldHint}>{t.shift.businessHint}</span>
              </label>

              {mode === "tipout" && (
                <div className={styles.rulesBlock}>
                  <p className={styles.subhead}>{t.rules.title}</p>
                  {!rules.length && <p className={styles.explainer}>{t.rules.empty}</p>}
                  <ul className={styles.ruleList}>
                    {rules.map((rule) => (
                      <li key={rule.id} className={styles.ruleRow}>
                        <select
                          id={`f-ruleSelf:${rule.id}`}
                          className={`${styles.input} ${styles.ruleSelect}`}
                          value={rule.fromRoleId}
                          onChange={(e) => updateRule(rule.id, { fromRoleId: e.target.value })}
                          aria-label={`${t.rules.pays}: ${t.roster.role}`}
                        >
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name || "?"}
                            </option>
                          ))}
                        </select>
                        <span className={styles.ruleWord}>{t.rules.pays}</span>
                        <input
                          id={`f-pct:${rule.id}`}
                          className={`${styles.input} ${styles.pctInput} ${issueClass(`pct:${rule.id}`)}`}
                          type="text"
                          inputMode="decimal"
                          enterKeyHint="next"
                          value={rule.pct}
                          onChange={(e) => updateRule(rule.id, { pct: e.target.value })}
                          onKeyDown={onFieldKeyDown}
                          aria-label="Percent"
                          aria-invalid={
                            issues[`pct:${rule.id}`] &&
                            issues[`pct:${rule.id}`].level === "error"
                              ? "true"
                              : undefined
                          }
                        />
                        <span className={styles.ruleWord}>%</span>
                        <select
                          className={`${styles.input} ${styles.basisSelect}`}
                          value={rule.basis}
                          onChange={(e) => updateRule(rule.id, { basis: e.target.value })}
                          aria-label="Basis"
                        >
                          {BASIS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label()}
                            </option>
                          ))}
                        </select>
                        <span className={styles.ruleWord}>{t.rules.to}</span>
                        <select
                          className={`${styles.input} ${styles.ruleSelect}`}
                          value={rule.toRoleId}
                          onChange={(e) => updateRule(rule.id, { toRoleId: e.target.value })}
                          aria-label={`${t.rules.to}: ${t.roster.role}`}
                        >
                          {roles.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name || "?"}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => removeRule(rule.id)}
                          aria-label={t.roster.remove}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                        {issues[`pct:${rule.id}`] && (
                          <p
                            className={
                              issues[`pct:${rule.id}`].level === "error"
                                ? styles.ruleError
                                : styles.ruleWarn
                            }
                          >
                            {issues[`pct:${rule.id}`].message}
                          </p>
                        )}
                        {issues[`ruleSelf:${rule.id}`] && (
                          <p className={styles.ruleError}>
                            {issues[`ruleSelf:${rule.id}`].message}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                  <button type="button" className={styles.addBtn} onClick={addRule}>
                    <FiPlus aria-hidden="true" /> {t.rules.add}
                  </button>
                </div>
              )}
            </div>

            {/* Step 2 — roster */}
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <p className={styles.panelLabel}>
                  <span className={styles.fig}>Fig. 03</span>
                  <span className={styles.figDash}>/</span> {t.steps.crew}
                </p>
                <button
                  type="button"
                  className={styles.collapseBtn}
                  onClick={() => setRosterOpen((open) => !open)}
                  aria-expanded={rosterOpen}
                >
                  {rosterOpen ? t.roster.hide : t.roster.show}
                  {rosterOpen ? (
                    <FiChevronUp aria-hidden="true" />
                  ) : (
                    <FiChevronDown aria-hidden="true" />
                  )}
                </button>
              </div>

              {rosterOpen ? (
                <>
                  <p className={styles.panelSub}>{t.steps.crewSub[mode]}</p>

                  {!hasStorage && (
                    <p className={styles.warning}>
                      <FiAlertTriangle aria-hidden="true" /> {t.roster.noStorage}
                    </p>
                  )}

                  {/* Column headers, desktop only. On a phone the row wraps to two
                      lines, so each control carries its own label instead. */}
                  {roster.length > 0 && (
                    <div className={styles.rosterHead} aria-hidden="true">
                      <span className={styles.headCenter}>{t.roster.workedShort}</span>
                      <span>{t.roster.name}</span>
                      <span>{t.roster.role}</span>
                      <span className={styles.headCenter}>{t.roster.points}</span>
                      <span />
                    </div>
                  )}

                  <ul className={styles.rosterList}>
                    {roster.map((p, i) => (
                      <li key={p.id} className={styles.rosterRow}>
                        {/* The tick had no caption on a phone, where the column
                            headers are gone, so it was a bare box to guess at. */}
                        <label className={styles.workedBox}>
                          <input
                            type="checkbox"
                            checked={(entries[p.id] || blankEntry()).working}
                            onChange={(e) => updateEntry(p.id, { working: e.target.checked })}
                            aria-label={`${t.roster.working}: ${displayName(p, i)}`}
                          />
                          <span className={styles.cellLabel}>{t.roster.workedShort}</span>
                        </label>
                        <input
                          className={`${styles.input} ${styles.nameInput}`}
                          type="text"
                          value={p.name}
                          placeholder={t.roster.namePlaceholder}
                          enterKeyHint="next"
                          ref={(el) => {
                            nameRefs.current[p.id] = el;
                          }}
                          onChange={(e) => updatePerson(p.id, { name: e.target.value })}
                          onKeyDown={onFieldKeyDown}
                          aria-label={`${t.roster.name}: ${displayName(p, i)}`}
                        />
                        <label className={styles.roleCell}>
                          <span className={styles.cellLabel}>{t.roster.role}</span>
                          <select
                            className={`${styles.input} ${styles.roleSelect}`}
                            value={p.roleId}
                            onChange={(e) => changeRole(p.id, e.target.value)}
                            aria-label={`${t.roster.role}: ${displayName(p, i)}`}
                          >
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name || "?"}
                              </option>
                            ))}
                          </select>
                        </label>
                        {/* Points are a multiplier on a share of a pool. If no
                            rule pays this person's role out of one, the box
                            goes flat instead of pretending to be live. */}
                        {pointsMatter(p) ? (
                          <label className={styles.pointsCell}>
                            <span className={styles.cellLabel}>{t.roster.points}</span>
                            <input
                              id={`f-points:${p.id}`}
                              className={`${styles.input} ${styles.pointsInput} ${issueClass(`points:${p.id}`)}`}
                              type="text"
                              inputMode="decimal"
                              enterKeyHint="next"
                              value={p.points}
                              onChange={(e) => updatePerson(p.id, { points: e.target.value })}
                              onKeyDown={onFieldKeyDown}
                              aria-label={`${t.roster.points}: ${displayName(p, i)}`}
                              aria-invalid={
                                issues[`points:${p.id}`] &&
                                issues[`points:${p.id}`].level === "error"
                                  ? "true"
                                  : undefined
                              }
                            />
                          </label>
                        ) : (
                          <div className={styles.pointsCell}>
                            <span className={styles.cellLabel}>{t.roster.points}</span>
                            <span className={styles.pointsIdle} title={t.roster.pointsUnusedWhy}>
                              {t.roster.pointsUnused}
                            </span>
                          </div>
                        )}
                        <button
                          type="button"
                          className={styles.iconBtn}
                          onClick={() => removePerson(p.id)}
                          aria-label={`${t.roster.remove}: ${displayName(p, i)}`}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                        {issues[`points:${p.id}`] && (
                          <p
                            className={
                              issues[`points:${p.id}`].level === "error"
                                ? styles.rowIssue
                                : styles.rowIssueWarn
                            }
                          >
                            {issues[`points:${p.id}`].message}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>

                  {!roster.length && <p className={styles.hint}>{t.roster.empty}</p>}

                  <button type="button" className={styles.addBtn} onClick={addPerson}>
                    <FiPlus aria-hidden="true" /> {t.roster.add}
                  </button>

                  <p className={styles.explainer}>{t.roster.pointsExplainer}</p>

                  <details
                    className={styles.details}
                    open={rolesOpen}
                    onToggle={(e) => setRolesOpen(e.currentTarget.open)}
                  >
                    <summary className={styles.summary}>{t.roster.manageRoles}</summary>
                    <ul className={styles.roleList}>
                      {roles.map((r) => (
                        <li key={r.id} className={styles.roleRow}>
                          <input
                            className={`${styles.input} ${styles.roleNameInput}`}
                            type="text"
                            value={r.name}
                            enterKeyHint="next"
                            onChange={(e) => updateRole(r.id, { name: e.target.value })}
                            onKeyDown={onFieldKeyDown}
                            aria-label={t.roster.roleName}
                          />
                          <input
                            id={`f-rolePoints:${r.id}`}
                            className={`${styles.input} ${styles.pointsInput} ${issueClass(`rolePoints:${r.id}`)}`}
                            type="text"
                            inputMode="decimal"
                            enterKeyHint="next"
                            value={r.defaultPoints}
                            onChange={(e) => updateRole(r.id, { defaultPoints: e.target.value })}
                            onKeyDown={onFieldKeyDown}
                            aria-label={`${t.roster.roleDefaultPoints}: ${r.name}`}
                          />
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => removeRole(r.id)}
                            disabled={roleInUse(r.id)}
                            title={roleInUse(r.id) ? t.roster.roleInUse : t.roster.remove}
                            aria-label={`${t.roster.remove}: ${r.name}`}
                          >
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button type="button" className={styles.addBtn} onClick={addRole}>
                      <FiPlus aria-hidden="true" /> {t.roster.addRole}
                    </button>
                  </details>
                </>
              ) : (
                <p className={styles.panelSummary}>
                  {t.roster.collapsed(workingPeople.length, roster.length)}
                </p>
              )}
            </div>

            {/* Step 3 — the shift */}
            <div className={styles.panel}>
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 04</span>
                <span className={styles.figDash}>/</span> {t.steps.shift}
              </p>
              <p className={styles.panelSub}>{t.steps.shiftSub[mode]}</p>

              {/* Tip-out asks for nothing until the rules say what to ask for,
                  which is the one place this tool reads as broken if unsaid. */}
              {mode === "tipout" && !rules.length ? (
                <p className={styles.explainer}>{t.shift.needRules}</p>
              ) : !workingPeople.length ? (
                <p className={styles.hint}>{t.shift.noWorkers}</p>
              ) : (
                <>
                  {hoursWanted.length > 1 && (
                    <div className={styles.bulkRow}>
                      <label className={styles.field}>
                        <span className={styles.fieldLabel}>{t.shift.sameHours}</span>
                        <input
                          className={styles.input}
                          type="text"
                          inputMode="decimal"
                          enterKeyHint="done"
                          value={bulkHours}
                          placeholder="0"
                          onChange={(e) => setBulkHours(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            applyHoursToAll();
                          }}
                          aria-label={t.shift.sameHours}
                        />
                      </label>
                      <button
                        type="button"
                        className={styles.bulkBtn}
                        onClick={applyHoursToAll}
                        disabled={!bulkHours.trim()}
                      >
                        {t.shift.applyAll}
                      </button>
                    </div>
                  )}

                  {progress.asked > 1 && (
                    <p className={styles.progress}>
                      {t.shift.progress(progress.done, progress.asked)}
                    </p>
                  )}

                  <ul className={styles.entryList}>
                    {workingPeople.map((p) => {
                      const entry = entries[p.id] || blankEntry();
                      const index = roster.indexOf(p);
                      const fields = fieldsFor(p);

                      return (
                        <li key={p.id} className={styles.entryRow}>
                          <div className={styles.entryWho}>
                            {progress.filled.has(p.id) && (
                              <FiCheck className={styles.entryDone} aria-hidden="true" />
                            )}
                            <span className={styles.entryName}>{displayName(p, index)}</span>
                            <span className={styles.entryRole}>{roleName(p.roleId)}</span>
                          </div>
                          <div className={styles.entryFields}>
                            {fields.hours &&
                              numberField(t.roster.hours, entry.hours, (v) =>
                                updateEntry(p.id, { hours: v }),
                                // Tip-out pays a support pool to whoever worked;
                                // hours only sharpen the split if you give them.
                                { issueKey: `hours:${p.id}`, optional: fields.optional.hours }
                              )}
                            {fields.tips &&
                              numberField(
                                // Money handed straight to the bar is not the
                                // same thing as the tip-out it is owed, and the
                                // two land on the same person, so the box says
                                // which one it is asking for.
                                fields.optional.tips ? t.roster.ownTips : t.roster.tips,
                                entry.tips,
                                (v) => updateEntry(p.id, { tips: v }),
                                { issueKey: `tips:${p.id}`, money: true, optional: fields.optional.tips }
                              )}
                            {fields.food &&
                              numberField(t.roster.foodSales, entry.food, (v) =>
                                updateEntry(p.id, { food: v }), { issueKey: `food:${p.id}`, money: true }
                              )}
                            {fields.liquor &&
                              numberField(t.roster.liquorSales, entry.liquor, (v) =>
                                updateEntry(p.id, { liquor: v }), { issueKey: `liquor:${p.id}`, money: true }
                              )}
                            {fields.sales &&
                              numberField(t.roster.sales, entry.sales, (v) =>
                                updateEntry(p.id, { sales: v }), { issueKey: `sales:${p.id}`, money: true }
                              )}
                            {fields.idle && <span className={styles.idleNote}>{t.shift.noRulesForRole}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>

            {/* Step 4 — the split */}
            <div className={styles.panel} ref={resultsRef}>
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 05</span>
                <span className={styles.figDash}>/</span> {t.steps.split}
              </p>
              <p className={styles.panelSub}>{t.steps.splitSub[mode]}</p>

              {(error || problem) && (
                <p className={styles.error}>
                  <FiAlertTriangle aria-hidden="true" /> {error || problem}
                </p>
              )}

              {!error && !problem && !result && (
                <p className={styles.hint}>{emptyHint || t.results.empty}</p>
              )}

              {!error && result && mode === "pool" && (
                <>
                  <div className={styles.summaryGrid}>
                    <div>
                      <span className={styles.summaryLabel}>{t.results.poolLabel}</span>
                      <span className={styles.summaryValue}>{money(result.poolCents)}</span>
                    </div>
                    <div>
                      <span className={styles.summaryLabel}>{t.results.totalWeighted}</span>
                      <span className={styles.summaryValue}>
                        {formatHundredths(result.totalWeightedHoursCenti)}
                      </span>
                    </div>
                    <div>
                      <span className={styles.summaryLabel}>{t.results.rate}</span>
                      {/* Two decimals to read, the exact figure underneath so it
                          still reconciles against the printed receipt. */}
                      <span className={styles.summaryValue}>
                        ${result.ratePerWeightedHour.toFixed(2)}
                      </span>
                      <span className={styles.summaryExact}>
                        {t.results.rateExact(`$${result.ratePerWeightedHour.toFixed(6)}`)}
                      </span>
                    </div>
                  </div>

                  <ul className={styles.resultList}>
                    {result.rows.map((r) => (
                      <li key={r.id} className={styles.resultRow}>
                        <div className={styles.resultWho}>
                          <span className={styles.resultName}>{r.name}</span>
                          <span className={styles.resultRole}>{r.role}</span>
                        </div>
                        <div className={styles.resultMath}>
                          {formatHundredths(r.hoursCenti)} × {formatHundredths(r.pointsCenti)} ={" "}
                          {formatHundredths(r.weightedHoursCenti)} {t.results.weighted.toLowerCase()}
                        </div>
                        {amount(r.shareCents, styles.resultShare)}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {!error && result && mode === "tipout" && (
                <>
                  <p className={styles.subhead}>{t.results.contributors}</p>
                  <ul className={styles.resultList}>
                    {result.contributors.map((c) => (
                      <li
                        key={c.id}
                        className={`${styles.contributorCard} ${c.overpaid ? styles.contributorBad : ""}`}
                      >
                        <div className={styles.resultWho}>
                          <span className={styles.resultName}>{c.name}</span>
                          <span className={styles.resultRole}>{c.roleName}</span>
                          {c.overpaid && (
                            <span className={styles.badFlag}>{t.results.overpaid}</span>
                          )}
                        </div>
                        <div className={styles.ledger}>
                          <div className={styles.ledgerRow}>
                            <span>{t.results.tips}</span>
                            <span>{money(c.tipsCents)}</span>
                          </div>
                          {c.deductions.map((d) => (
                            <div key={d.ruleId} className={styles.ledgerRow}>
                              <span>
                                {formatHundredths(d.pctCenti)}%{" "}
                                {BASIS_OPTIONS.find((o) => o.value === d.basis).label()}{" "}
                                {t.rules.to} {d.toRoleName}
                              </span>
                              <span className={styles.negative}>-{money(d.amountCents)}</span>
                            </div>
                          ))}
                          <div className={`${styles.ledgerRow} ${styles.ledgerTotal}`}>
                            <span>{t.results.takeHome}</span>
                            {amount(c.takeHomeCents)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {result.valid && result.pools.length > 0 && (
                    <>
                      <p className={styles.subhead}>{t.results.support}</p>
                      {result.pools.map((pool) => (
                        <div key={pool.roleId} className={styles.poolBlock}>
                          <div className={styles.poolHead}>
                            <span className={styles.resultName}>{pool.roleName}</span>
                            <span className={styles.poolAmount}>
                              {t.results.poolReceived} {money(pool.poolCents)}
                            </span>
                          </div>
                          <ul className={styles.resultList}>
                            {pool.recipients.map((r) => (
                              <li key={r.id} className={styles.resultRow}>
                                <div className={styles.resultWho}>
                                  <span className={styles.resultName}>{r.name}</span>
                                </div>
                                <div className={styles.resultMath}>
                                  {formatHundredths(r.hoursCenti)} × {formatHundredths(r.pointsCenti)}{" "}
                                  = {formatHundredths(r.weightedHoursCenti)}{" "}
                                  {t.results.weighted.toLowerCase()}
                                </div>
                                {amount(r.shareCents, styles.resultShare)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </>
                  )}

                  {result.valid && (
                    <p className={styles.conservation}>
                      {t.results.conservation(
                        money(result.totalTipsCents),
                        money(result.totalTakeHomeCents),
                        money(result.totalSharesCents)
                      )}
                    </p>
                  )}
                </>
              )}

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={!canPrint}
                  onClick={handleShare}
                >
                  {copied ? <FiCheck aria-hidden="true" /> : <FiShare2 aria-hidden="true" />}{" "}
                  {copied ? t.results.copied : t.results.share}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={!canPrint || busy}
                  onClick={handleDownload}
                >
                  <FiDownload aria-hidden="true" /> {busy ? t.results.working : t.results.download}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={!canPrint}
                  onClick={() => setPendingPrint("breakdown")}
                >
                  <FiPrinter aria-hidden="true" /> {t.results.print}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  disabled={!workingPeople.length}
                  onClick={() => setPendingPrint("blank")}
                >
                  <FiPrinter aria-hidden="true" /> {t.results.printBlank}
                </button>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setConfirmReset(true)}
                >
                  <FiRotateCcw aria-hidden="true" /> {t.results.reset}
                </button>
              </div>

              {/* The confirm used to appear in the slot the trigger was in, so a
                  quick double tap cleared the shift. It gets its own row now,
                  and clearing is undoable anyway. */}
              {confirmReset && (
                <div className={styles.confirmRow}>
                  <p className={styles.confirmText}>{t.results.resetQuestion}</p>
                  <div className={styles.confirmButtons}>
                    <button
                      type="button"
                      className={styles.ghostBtn}
                      onClick={() => setConfirmReset(false)}
                    >
                      {t.results.cancel}
                    </button>
                    <button type="button" className={styles.dangerBtn} onClick={resetShift}>
                      {t.results.resetConfirm}
                    </button>
                  </div>
                </div>
              )}

              <p className={styles.disclaimer}>{t.disclaimer}</p>
            </div>

            {/* Rides the bottom of the workspace and scrolls away with it. The
                text is one span in both states, so the live region exists
                before it has anything to say and actually gets announced. */}
            <div className={`${styles.stickyBar} ${bar.tone}`}>
              <span className={styles.barText} role="status">
                {undo ? undo.message : bar.text}
              </span>
              {undo ? (
                <button type="button" className={styles.barBtn} onClick={runUndo}>
                  <FiCornerUpLeft aria-hidden="true" /> {t.results.undo}
                </button>
              ) : (
                bar.action && (
                  <button type="button" className={styles.barBtn} onClick={bar.onClick}>
                    {bar.action} <FiArrowDown aria-hidden="true" />
                  </button>
                )
              )}
            </div>
          </div>
        </section>

        {/* ── Feedback ──
            Sits against the bottom of the workspace on purpose: somebody who
            just found a split this cannot write is looking right here. It
            opens over the page rather than navigating away, so the thing they
            were about to describe is still on screen behind it. */}
        <section className={styles.feedback}>
          <div className={styles.feedbackInner}>
            <p className={styles.feedbackEyebrow}>{t.feedback.eyebrow}</p>
            <p className={styles.feedbackText}>{t.feedback.text}</p>
            <button
              type="button"
              className={styles.feedbackBtn}
              ref={feedbackBtnRef}
              onClick={() => setFeedbackOpen(true)}
            >
              {t.feedback.button} <FiArrowRight aria-hidden="true" />
            </button>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className={styles.faq}>
          <div className={styles.faqInner}>
            <motion.div
              className={styles.sectionHead}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className={styles.panelLabel}>
                <span className={styles.fig}>Fig. 06</span>
                <span className={styles.figDash}>/</span> {t.faqTitle}
              </p>
            </motion.div>
            <div className={styles.faqList}>
              {t.faqs.map((f) => (
                <details key={f.q} className={styles.faqItem}>
                  <summary className={styles.faqQ}>{f.q}</summary>
                  <p className={styles.faqA}>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className={styles.cta}>
          <motion.div
            className={styles.ctaInner}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <p className={styles.ctaEyebrow}>{t.cta.eyebrow}</p>
            <h2 className={styles.ctaTitle}>
              {t.cta.titleLine1}
              <br />
              <em>{t.cta.titleLine2}</em>
            </h2>
            <p className={styles.ctaSub}>{t.cta.sub}</p>
            <a href="/#start" className={styles.ctaPrimary}>
              {t.cta.button}
            </a>
          </motion.div>
        </section>

        {/* Inside .page so the print stylesheet takes it away with everything
            else, and closing hands the thumb back to the button that opened it. */}
        <ToolFeedback
          open={feedbackOpen}
          onClose={() => setFeedbackOpen(false)}
          toolName="Tip Split Calculator"
          labels={t.feedback.form}
          returnFocusTo={feedbackBtnRef}
        />
      </main>

      {/* The printed receipt. Off screen until the print stylesheet takes over,
          so what prints is the same monospace block the PDF draws. */}
      <pre className={styles.receipt} aria-hidden="true">
        {receiptLines.map((line, i) =>
          line.bold ? (
            <b key={i}>{`${line.text}\n`}</b>
          ) : (
            <React.Fragment key={i}>{`${line.text}\n`}</React.Fragment>
          )
        )}
      </pre>
    </>
  );
};

export default TipSplitPage;
